"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Label } from "@panda0/panda0-ui";
import { cn } from "@panda0/panda0-utils";
import { Clock, Leaf, Rocket, Sparkles, Star, Trophy, Zap } from "lucide-react";

interface YearsExperienceControlProps {
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly defaultMin?: number;
  readonly defaultMax?: number;
  readonly onChange?: (min: number, max: number) => void;
  readonly className?: string;
}

function YearsExperienceControl({
  min = 0,
  max = 20,
  step = 1,
  defaultMin = 2,
  defaultMax = 5,
  onChange,
  className,
}: YearsExperienceControlProps) {
  // Use state with a stable initial value instead of taking it from props on every render
  const [values, setValues] = useState<[number, number]>([
    defaultMin,
    defaultMax,
  ]);
  const [dragging, setDragging] = useState<"min" | "max" | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Memoize this array to prevent unnecessary re-renders
  const experienceLevels = useMemo(
    () => [
      {
        name: "Entry",
        range: "0-1 yr",
        min: 0,
        max: 1,
        icon: Leaf,
        color: "#000000",
      },
      {
        name: "Junior",
        range: "1-2 yrs",
        min: 1,
        max: 2,
        icon: Rocket,
        color: "#000000",
      },
      {
        name: "Mid",
        range: "3-4 yrs",
        min: 3,
        max: 4,
        icon: Zap,
        color: "#000000",
      },
      {
        name: "Senior",
        range: "5-7 yrs",
        min: 5,
        max: 7,
        icon: Star,
        color: "#000000",
      },
      {
        name: "Lead",
        range: "8-11 yrs",
        min: 8,
        max: 11,
        icon: Sparkles,
        color: "#000000",
      },
      {
        name: "Principal",
        range: "12+ yrs",
        min: 12,
        max: 20,
        icon: Trophy,
        color: "#000000",
      },
    ],
    []
  );

  // Find the initial active level based on the default value
  const activeLevel =
    experienceLevels.find(
      (level) => values[1] >= level.min && values[1] <= level.max
    ) || experienceLevels[1];

  const handleMouseDown = (type: "min" | "max") => (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(type);
  };

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  const calculateValueFromPosition = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return 0;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );
      const rawValue = min + percentage * (max - min);
      return Math.round(rawValue / step) * step;
    },
    [min, max, step]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return;

      const newValue = calculateValueFromPosition(e.clientX);

      setValues((prev) => {
        let newValues: [number, number] = [...prev];

        if (dragging === "min") {
          if (newValue < prev[1]) {
            newValues = [newValue, prev[1]];
          }
        } else if (dragging === "max") {
          if (newValue > prev[0]) {
            newValues = [prev[0], newValue];
          }
        }

        onChange?.(newValues[0], newValues[1]);
        return newValues;
      });
    },
    [dragging, calculateValueFromPosition, onChange]
  );

  const handleBoxClick = (minValue: number, maxValue: number) => {
    setValues([minValue, maxValue]);
    onChange?.(minValue, maxValue);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;

    const newValue = calculateValueFromPosition(e.clientX);

    const distToMin = Math.abs(newValue - values[0]);
    const distToMax = Math.abs(newValue - values[1]);

    if (distToMin <= distToMax) {
      setValues([newValue, values[1]]);
      onChange?.(newValue, values[1]);
    } else {
      setValues([values[0], newValue]);
      onChange?.(values[0], newValue);
    }
  };

  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  const minThumbPosition = ((values[0] - min) / (max - min)) * 100;
  const maxThumbPosition = ((values[1] - min) / (max - min)) * 100;

  const activeColor = activeLevel.color;

  return (
    <div className={cn("rounded-xl bg-white", className)}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-gray-700">
          Experience *
        </Label>
        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-base font-bold text-gray-800">
            {values[0]}-{values[1]} years
          </span>
        </div>
      </div>

      <div
        ref={sliderRef}
        className="relative w-full h-4 bg-gray-100 rounded-full mt-2 cursor-pointer shadow-inner"
        onClick={handleTrackClick}
      >
        <div
          className="absolute h-full rounded-full transition-all duration-300 ease-out"
          style={{
            left: `${minThumbPosition}%`,
            width: `${maxThumbPosition - minThumbPosition}%`,
            background: `linear-gradient(90deg, ${activeColor}88, ${activeColor})`,
          }}
        />

        <div
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={values[0]}
          tabIndex={0}
          onMouseDown={handleMouseDown("min")}
          className={cn(
            "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white p-2.5 shadow-lg transition-all duration-200",
            "hover:scale-125 hover:shadow-xl",
            dragging === "min" ? "cursor-grabbing scale-125" : "cursor-grab"
          )}
          style={{
            left: `${minThumbPosition}%`,
            background: `linear-gradient(135deg, white, ${activeColor})`,
            boxShadow: `0 4px 12px ${activeColor}66`,
          }}
        />

        <div
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={values[1]}
          tabIndex={0}
          onMouseDown={handleMouseDown("max")}
          className={cn(
            "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white p-2.5 shadow-lg transition-all duration-200",
            "hover:scale-125 hover:shadow-xl",
            dragging === "max" ? "cursor-grabbing scale-125" : "cursor-grab"
          )}
          style={{
            left: `${maxThumbPosition}%`,
            background: `linear-gradient(135deg, white, ${activeColor})`,
            boxShadow: `0 4px 12px ${activeColor}66`,
          }}
        />
      </div>

      <div className="flex justify-between mt-2 text-xs text-gray-500 w-full px-1">
        <span>{min}</span>
        <span>{Math.floor(max / 4)}</span>
        <span>{Math.floor(max / 2)}</span>
        <span>{Math.floor((3 * max) / 4)}</span>
        <span>{`${max}+`}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mt-6">
        {experienceLevels.map((level) => {
          const isActive = level.name === activeLevel.name;
          const Icon = level.icon;
          return (
            <button
              key={level.name}
              onClick={() => handleBoxClick(level.min, level.max)}
              className={cn(
                "p-4 rounded-xl text-center transition-all duration-300 border-2 group",
                isActive
                  ? "bg-gradient-to-br from-gray-900 to-gray-800 text-white border-gray-700 shadow-lg"
                  : "bg-gray-50 text-gray-700 border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-md"
              )}
              style={{
                boxShadow: isActive ? `0 8px 16px ${level.color}33` : "",
              }}
            >
              <div className="flex justify-center mb-2 transition-transform duration-300 group-hover:scale-110">
                <Icon
                  className={cn(
                    "w-6 h-6 transition-colors",
                    isActive
                      ? "text-white"
                      : `text-gray-600 group-hover:text-[${level.color}]`
                  )}
                  style={{ color: isActive ? "white" : level.color }}
                />
              </div>
              <div
                className={cn(
                  "text-sm font-medium",
                  isActive ? "text-white" : "text-gray-800"
                )}
              >
                {level.name}
              </div>
              <div
                className={cn(
                  "text-xs mt-1 opacity-80",
                  isActive ? "text-gray-300" : "text-gray-500"
                )}
              >
                {level.range}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(YearsExperienceControl);
